import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getDrugs, resetError, resetAllDrugList } from "./AllDrugs.action";

import { ColumnTypes, FilterTypes } from "commons/table/Constants";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import ResetListState from "components/ResetListState";
import Tip from "components/Tip";
import CAT_CONSTANTS from "lookup/CatConstants";
import { showNotification } from "services/NotificationService";

const DEFAULT_PAGE_SIZE = 15;

const AllDrugList = (props) => {
  const dispatch = useDispatch();

  const { data, pages, loading, listState, isError, totalElements } =
    useSelector((state) => {
      let ls = state.allDrugList?.listState;
      if (!ls) {
        ls = {
          page: 0,
          pageSize: DEFAULT_PAGE_SIZE,
          sorted: CAT_CONSTANTS.defaultMedDrugsSort,
          filtered: [{ id: "brandGenType", value: "All" }],
          resized: [],
          expanded: {},
        };
      }
      return {
        data:
          state.allDrugList.data !== undefined ? state.allDrugList.data : [],
        pages: state.allDrugList.pages ? state.allDrugList.pages : 0,
        loading: state.allDrugList.loading,
        listState: ls,
        isError: state.allDrugList.isError,
        totalElements: state.allDrugList.totalElements,
      };
    });

  const { isSelectRow, showResetListButton, placedOnLightBg, onSelect } = props;

  const updateData = useCallback(
    (state) => {
      const nextState = {
        ...state,
        sorted: [...state.sorted],
        filtered: [...state.filtered],
      };

      if (nextState.sorted !== CAT_CONSTANTS.defaultMedDrugsSort) {
        nextState.sorted.forEach((element) => {
          element.isDefault = false;
        });
      }

      const indexOfBgDefaultFilter =
        nextState.filtered && nextState.filtered.length > 0
          ? nextState.filtered.findIndex(
              (x) => x.id == "brandGenType" && x.value == "All"
            )
          : -1;
      if (indexOfBgDefaultFilter > -1) {
        nextState.filtered.splice(indexOfBgDefaultFilter, 1);
      }

      dispatch(
        getDrugs(
          nextState.pageSize,
          nextState.page,
          nextState.sorted,
          nextState.filtered,
          nextState.resized,
          nextState.expanded
        )
      );
    },
    [dispatch]
  );

  // initial load, mimic old componentDidMount behavior
  useEffect(() => {
    updateData(listState);
  }, []);

  useEffect(() => {
    if (isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      dispatch(resetError());
    }
  }, [isError, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetAllDrugList());
    };
  }, [dispatch]);

  const renderStatusCell = (status) => {
    return (
      <span
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        {status}
      </span>
    );
  };

  const isSelected = (selectProps) => {
    let drugDsoId = ("" + selectProps).replace("select-", "");
    let { selected } = props;
    if (selected?.drugDsoId && selected.drugDsoId == drugDsoId) return true;
    return false;
  };

  const toggleSelection = (selectProps) => {
    let drugDsoId = selectProps.replace("select-", "");
    let selectedRow = {};
    let selectedMed = props.selected?.drugDsoId ? props.selected.drugDsoId : -1;
    if (selectedMed != drugDsoId) {
      // add to the object
      selectedRow = data.find((x) => x.drugDsoId == drugDsoId);
    }
    if (onSelect) {
      onSelect(selectedRow);
    }
  };

  const columns = [
    {
      Header: "Drug Id",
      accessor: "drugDsoId",
      accessorKey: "drugDsoId",
      width: 60,
      show: false,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Text,
    },
    {
      header: "Drug Name",
      accessorKey: "medName",
      accessor: "medName",
      width: 250,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "B/G Code",
      accessor: "brandGenType",
      accessorKey: "brandGenType",
      width: 80,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SingleSelect,
      filterOptions: CAT_CONSTANTS.bgCodeFilter,
    },
    {
      header: "Ingredient",
      accessor: "ingredient",
      accessorKey: "ingredient",
      width: 300,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "GPI Code",
      accessor: "gpiCode",
      accessorKey: "gpiCode",
      width: 300,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Form",
      accessor: "medForm",
      accessorKey: "medForm",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Route of Administration",
      accessor: "routeOfAdministration",
      accessorKey: "routeOfAdministration",
      width: 130,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Dynamic Switch Group",
      accessor: "dynamicSwitchGroupNames",
      accessorKey: "dynamicSwitchGroupNames",
      width: 200,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        return renderAllDsgs(row.original.dynamicSwitchGroupNames);
      },
    },
    {
      header: "On-market Strengths",
      accessor: "onMarketStrengths",
      accessorKey: "onMarketStrengths",
      width: 300,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Text,
    },
    {
      header: "Off-market Strengths",
      accessor: "offMarketStrengths",
      accessorKey: "offMarketStrengths",
      width: 300,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Text,
    },
  ];

  const onUpdateTable = (filterValues, sortBy, pageSize, pageNo) => {
    const listState = {
      page: pageNo,
      pageSize: pageSize,
      sorted: sortBy ? sortBy : [],
      filtered: filterValues,
      resized: [],
      expanded: {},
    };
    updateData(listState);
  };

  const renderDsgCell = (id, dsgName, shouldAddCommas) => {
    return (
      <Link
        to={`/cat/dynamic-switch-groups/${id}?showDisplayCodexBar=false`}
        className={"space-pre-wrap"}
      >
        {dsgName + (shouldAddCommas ? " , " : "")}
      </Link>
    );
  };

  const renderAllDsgs = (dynamicSwitchGroupNames) => {
    let dsgs = dynamicSwitchGroupNames
      ? Object.keys(dynamicSwitchGroupNames)
      : [];
    if (dsgs.length > 0) {
      return (
        <span
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {dsgs.map((key, index) => {
            return renderDsgCell(
              key,
              dynamicSwitchGroupNames[key],
              index + 1 != dsgs.length
            );
          })}
        </span>
      );
    }
    return (
      <span
        style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}
      >
        {" "}
        {"--"}{" "}
      </span>
    );
  };

  const onRowSelect = (selectedRows) => {
    if (Object.keys(selectedRows).length > 0) {
      const rowId = Object.keys(selectedRows)[0];
      let selectedRow = data.find((x) => x.drugDsoId == rowId);
      if (selectedRow && onSelect) {
        onSelect(selectedRow);
        return;
      }
    }
    if (onSelect) {
      onSelect();
    }
  };

  return (
    <>
      {showResetListButton && (
        <div className="float-right tiny-right-spacer">
          <ResetListState
            placedOnLightBg={placedOnLightBg}
            onReset={() =>
              updateData({
                ...listState,
                filtered: [],
                sorted: CAT_CONSTANTS.defaultMedDrugsSort,
              })
            }
            resetterDisabled={
              listState.filtered.length === 0 &&
              listState.sorted === CAT_CONSTANTS.defaultMedDrugsSort
            }
          />
        </div>
      )}
      <ServerSideReactTable
        columnDefination={columns}
        data={data}
        currentPage={listState.page}
        sorted={listState.sorted}
        filters={listState.filtered}
        defaultPageSize={listState.pageSize}
        totalPages={pages}
        totalRecords={totalElements == "loading" ? 0 : totalElements}
        onUpdateTableData={onUpdateTable}
        onRowSelect={onRowSelect}
        loading={loading}
        overrideRowId={"drugDsoId"}
        resetTableSelection={
          props.resetTableSelection ? props.resetTableSelection : null
        }
        addRowSelection={isSelectRow}
        enableMultiRowSelection={false}
      />
      <Tip>Tip: Hold shift when sorting to multi-sort and disable sort!</Tip>
    </>
  );
};

export default AllDrugList;
